import React, { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, CheckCircle2, AlertTriangle, FlaskConical } from 'lucide-react';
import { sections, labs as labsApi } from '../services/api';

const STATUS_LABEL = { open: 'Open', draft: 'Draft', closed: 'Closed', archived: 'Archived' };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
// Labs worked on at once. Each is one server-side operation creating its
// environments one after another, so this bounds pod creates in flight.
const PARALLEL_LABS = 3;

/**
 * Put section students into the labs that were built for the section: enrol
 * them and create each one's environment, in every open or draft lab ticked.
 *
 * Shown after students are imported into a section (for exactly those
 * students; `quietIfNothingToDo` hides it when no lab needs anything), and by
 * "Sync to labs" (for the whole roster). The work runs on the server as each
 * lab's container operation, so closing the window does not stop it.
 *
 * studentIds: university IDs, or null for every active student on the roster.
 */
export default function SectionLabsStep({ sectionId, studentIds, quietIfNothingToDo = false, onClose }) {
  const client = useQueryClient();
  const headingId = useId();
  const { data, isLoading, error } = useQuery({
    queryKey: ['section-labs', sectionId, studentIds ? [...studentIds].sort().join(',') : 'roster'],
    queryFn: () => sections.labs(sectionId, studentIds),
  });
  const labs = data?.labs || [];
  const actionable = labs.filter((lab) => lab.canCreate && lab.missing.length > 0);
  const [selected, setSelected] = useState(null);
  const [runs, setRuns] = useState({});
  const [phase, setPhase] = useState('review');
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (data && selected === null) setSelected(new Set(actionable.map((lab) => lab.id)));
  }, [data, selected, actionable]);

  useEffect(() => {
    if (quietIfNothingToDo && data && actionable.length === 0) closeRef.current();
  }, [quietIfNothingToDo, data, actionable.length]);

  const who = studentIds
    ? (studentIds.length === 1 ? 'this student' : `these ${studentIds.length} students`)
    : 'the students on this roster';
  const title = studentIds
    ? `Add ${studentIds.length === 1 ? 'the new student' : `the ${studentIds.length} new students`} to this section's labs?`
    : "Create missing environments in this section's labs";

  const update = (id, patch) => setRuns((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const run = async (targets) => {
    setPhase('running');
    targets.forEach((lab) => update(lab.id, { state: 'queued', error: null, result: null }));
    const queue = [...targets];
    const worker = async () => {
      for (let lab = queue.shift(); lab; lab = queue.shift()) {
        update(lab.id, { state: 'working', processed: 0, total: lab.missing.length });
        try {
          const result = await labsApi.createEnvironments(lab.id, lab.missing, {
            onProgress: (operation) => update(lab.id, { state: 'working', processed: operation.processed, total: operation.total }),
            onWaiting: () => update(lab.id, { state: 'waiting' }),
          });
          update(lab.id, { state: result?.stats?.failed ? 'partial' : 'done', result });
        } catch (err) {
          update(lab.id, { state: 'failed', error: err.response?.data?.error || err.message });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL_LABS, targets.length) }, worker));
    setPhase('done');
    client.invalidateQueries({ queryKey: ['section-labs', sectionId] });
    client.invalidateQueries({ queryKey: ['labs'] });
  };

  const chosen = actionable.filter((lab) => selected?.has(lab.id));
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const results = Object.values(runs);
  const created = results.reduce((sum, r) => sum + (r.result?.stats?.created || 0), 0);
  const problems = actionable.filter((lab) => ['failed', 'partial'].includes(runs[lab.id]?.state));

  return (
    <section className="section-labs" aria-labelledby={headingId}>
      <h4 id={headingId} className="section-labs__title">
        <FlaskConical size={16} aria-hidden="true" /> {title}
      </h4>

      {isLoading && (
        <p className="section-labs__note"><Loader size={14} className="spinner" aria-hidden="true" /> Finding this section's labs…</p>
      )}
      {error && (
        <p role="alert" className="section-labs__error">
          This section's labs could not be loaded: {error.response?.data?.message || error.message}
        </p>
      )}
      {data && labs.length === 0 && (
        <p className="section-labs__note">No labs use this section yet. A lab created from it enrolls the whole roster.</p>
      )}
      {data && labs.length > 0 && actionable.length === 0 && phase === 'review' && (
        <p className="section-labs__note">Every open and draft lab already has an environment for {who}.</p>
      )}

      {phase === 'review' && actionable.length > 0 && (
        <>
          <p className="section-labs__note">
            {who.charAt(0).toUpperCase() + who.slice(1)} will be enrolled and get their own environment in each lab you tick.
            Starter code is copied in once each environment starts.
          </p>
          <ul className="section-labs__list">
            {labs.map((lab) => {
              const inputId = `${headingId}-${lab.id}`;
              const reason = !lab.canCreate
                ? (lab.closing ? 'Closing — no new environments' : `${STATUS_LABEL[lab.status] || lab.status} — no new environments`)
                : lab.missing.length === 0 ? 'All set' : null;
              return (
                <li key={lab.id} className="section-labs__row">
                  <input
                    type="checkbox"
                    id={inputId}
                    checked={!reason && !!selected?.has(lab.id)}
                    disabled={!!reason}
                    onChange={() => toggle(lab.id)}
                  />
                  <label htmlFor={inputId}>
                    <span className="section-labs__name">{lab.name}</span>
                    <span className={`section-labs__chip section-labs__chip--${lab.status}`}>{STATUS_LABEL[lab.status] || lab.status}</span>
                  </label>
                  <span className="section-labs__detail">
                    {reason || plural(lab.missing.length, 'environment') + ' to create'}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="section-labs__actions">
            <button type="button" className="btn" onClick={onClose}>Not now</button>
            <button type="button" className="btn btn-primary" disabled={chosen.length === 0} onClick={() => run(chosen)}>
              Add to {plural(chosen.length, 'lab')}
            </button>
          </div>
        </>
      )}

      {phase !== 'review' && (
        <>
          <ul className="section-labs__list" aria-live="polite">
            {actionable.filter((lab) => runs[lab.id]).map((lab) => {
              const r = runs[lab.id];
              let icon = <Loader size={14} className="spinner" aria-hidden="true" />;
              let text = 'Waiting to start';
              if (r.state === 'waiting') text = 'Waiting for another operation on this lab to finish';
              if (r.state === 'working') text = `Creating ${Math.min((r.processed || 0) + 1, r.total || 1)} of ${r.total}…`;
              if (r.state === 'done' || r.state === 'partial') {
                const s = r.result?.stats || {};
                icon = r.state === 'done'
                  ? <CheckCircle2 size={14} className="section-labs__ok" aria-hidden="true" />
                  : <AlertTriangle size={14} className="section-labs__warn" aria-hidden="true" />;
                text = [`Created ${s.created || 0}`, s.existing ? `${s.existing} already had one` : null,
                  s.failed ? `${s.failed} failed: ${r.result.failed?.[0]?.error || 'see the lab page'}` : null]
                  .filter(Boolean).join(' · ');
              }
              if (r.state === 'failed') {
                icon = <AlertTriangle size={14} className="section-labs__warn" aria-hidden="true" />;
                text = r.error;
              }
              return (
                <li key={lab.id} className="section-labs__row">
                  {icon}
                  <span className="section-labs__name">{lab.name}</span>
                  <span className="section-labs__detail">{text}</span>
                </li>
              );
            })}
          </ul>
          {phase === 'running' && (
            <p className="section-labs__note">You can close this window: the environments keep being created.</p>
          )}
          {phase === 'done' && (
            <p role="status" className="section-labs__note">
              {`Done. ${plural(created, 'environment')} created.`}
              {problems.length > 0 && ' Some could not be created; the reason is shown beside each lab.'}
            </p>
          )}
          <div className="section-labs__actions">
            {phase === 'done' && problems.length > 0 && (
              <button type="button" className="btn" onClick={() => run(problems)}>Retry {plural(problems.length, 'lab')}</button>
            )}
            <button type="button" className="btn btn-primary" onClick={onClose} disabled={phase === 'running'}>
              {phase === 'running' ? 'Working…' : 'Done'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
